import api from "api";
import { useToggleState } from "hooks";
import { makeAutoObservable, observable } from "mobx";
import { observer } from "mobx-react-lite";
import { useOrderQuery } from "queries/order";
import { FC } from "react";
import { CgSpinnerAlt } from "react-icons/cg";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { FiPrinter, FiSearch } from "react-icons/fi";
import ReactModal from "react-modal";
import { IOrder } from "types/order";

class OrderModalStore {
	orderData: IOrder | null = null;
	isOpen: boolean = false;

	constructor() {
		makeAutoObservable(this);
	}

	setOrderData(order: IOrder) {
		this.orderData = order;
	}

	toggleOpen = () => {
		this.isOpen = !this.isOpen;
	};
	open = () => {
		this.isOpen = true;
	};
	close = () => {
		this.isOpen = false;
	};
}

const orderModalStore = new OrderModalStore();

const OrderModal = observer(() => {
	return (
		<ReactModal
			isOpen={orderModalStore.isOpen}
			ariaHideApp={false}
			onRequestClose={orderModalStore.close}
			className="max-h-[80vh] min-w-[256px] w-[60vw] overflow-y-auto bg-gray-primary rounded p-4 pt-1 absolute-center"
		>
			<div className="flex justify-between mt-2">
				<div>Order Number: {orderModalStore.orderData?.orderNumber}</div>
				<div>User: {orderModalStore.orderData?.user?.firstName + " " + orderModalStore.orderData?.user?.lastName}</div>
				<div>Date: {new Date(orderModalStore.orderData?.createdAt || "").toDateString()}</div>
			</div>
			<div className="text-lg mt-5 mb-2 text-secondary">Transactions</div>
			<ul className="flex flex-col space-y-2 list-disc pl-5 text-secondary">
				{orderModalStore.orderData?.transactions?.map((transaction) => (
					<li key={transaction.id}>
						{transaction.menuItemName}
						{Object.keys(transaction.options).length ? (
							<>
								<div className="flex flex-col space-x-1">
									{Object.entries(transaction.options).map(([optionName, optionChoiceDetail]) => (
										<span key={optionName + optionChoiceDetail.name}>
											{optionName}: {optionChoiceDetail.name} [{optionChoiceDetail.price}pts]
										</span>
									))}
								</div>
							</>
						) : null}
						<div className="grid grid-cols-3 border border-secondary p-0.5">
							<span className="px-2">Original Price: {transaction.totalOriginalPrice.toFixed(2)}pts</span>
							<span className="border-l border-secondary px-2">Discount: {transaction.totalDiscount.toFixed(2)}pts</span>
							<span className="border-l border-secondary px-2">Total Price: {transaction.totalTransactionPrice.toFixed(2)}pts</span>
						</div>
					</li>
				))}
			</ul>

			<div className="mt-5 flex justify-end">
				<div className="flex justify-evenly divide-x divide-gray-200 border p-0.5">
					<span className="px-2">Original Price: {orderModalStore.orderData?.totalOriginalPrice.toFixed(2)}pts</span>
					<span className="px-2">Discount: {orderModalStore.orderData?.totalDiscount.toFixed(2)}pts</span>
					<span className="px-2">Total Price: {orderModalStore.orderData?.totalPricePaid.toFixed(2)}pts</span>
				</div>
			</div>
		</ReactModal>
	);
});

const OrderTableBody: FC<{ orders: IOrder[] | undefined }> = ({ orders }) => {
	const orderStatusColor = (color: IOrder["status"]) => {
		switch (color) {
			case "open":
				return "text-green-500";
			case "settled":
				return "text-red-500";
			case "refunded":
				return "text-yellow-500";
			default:
				return "text-gray-200";
		}
	};

	return (
		<>
			{orders?.map((order, index) => (
				<div key={order.id} className={`grid grid-cols-9 ${index !== orders?.length - 1 && "border-b border-gray-600"}`}>
					<span className="px-2 py-1.5 col-span-2">{new Date(order.createdAt).toLocaleDateString()}</span>
					<span className="px-2 py-1.5 col-span-2">{order.user.firstName + " " + order.user.lastName}</span>
					<span className="font-light px-2 py-1.5 col-span-2">{order.totalPricePaid.toFixed(2)}</span>
					<span className={`font-light px-2 py-1.5 col-span-2 ${orderStatusColor(order.status)}`}>{order.status}</span>
					<span className="px-2 py-1.5 flex justify-center space-x-1 items-center text-secondary col-span-1">
						<FiSearch
							className="cursor-pointer text-orange-500"
							onClick={() => {
								orderModalStore.open();
								orderModalStore.setOrderData(order);
							}}
						/>
						<FiPrinter className="cursor-pointer" onClick={() => api.order.print(order.id)} />
					</span>
				</div>
			))}
		</>
	);
};

const PastOrders: FC = () => {
	const {
		nextPage,
		currPage,
		prevPage,
		setFirstPage,
		setLastPage,
		query: { data: orders, isLoading },
	} = useOrderQuery();

	return (
		<>
			<OrderModal />
			<div className="p-7 h-full flex flex-col">
				<div className="text-right">
					<div className="border border-primary rounded inline-flex mb-2 divide-x divide-primary items-center">
						<button className="text-primary py-1.5 px-2" onClick={prevPage}>
							<FaChevronLeft />
						</button>
						<button className="text-primary py-1.5 px-2 font-bold" onClick={setFirstPage}>
							First
						</button>
						<span className="text-primary py-1.5 px-2 font-bold">{currPage}</span>
						<button className="text-primary py-1.5 px-2 font-bold" onClick={setLastPage}>
							Last
						</button>
						<button className="text-primary py-1.5 px-2" onClick={nextPage}>
							<FaChevronRight />
						</button>
					</div>
				</div>
				<div className="flex-1 max-h-full w-full border border-primary rounded overflow-hidden flex flex-col">
					<div className="grid grid-cols-9 divide-x divide-primary bg-primary rounded-t">
						<div className="col-span-2 px-2 py-1 text-white">Created At</div>
						<div className="col-span-2 px-2 py-1 text-white">User</div>
						<div className="col-span-2 px-2 py-1 text-white">Total Price</div>
						<div className="col-span-2 px-2 py-1 text-white">Status</div>
						<div className="col-span-1 px-2 py-1 text-white text-center"></div>
					</div>
					<div className="flex-1 max-h-full overflow-y-scroll">
						{isLoading ? (
							<div className="col-span-5 flex justify-center py-3 px-2">
								<CgSpinnerAlt className="animate-spin w-10 h-10 text-primary" />
							</div>
						) : (
							<OrderTableBody orders={orders?.items} />
						)}
					</div>
				</div>
			</div>
		</>
	);
};

export default PastOrders;
